/**
 * Juno Health Quiz Auto-Fill Script
 * 
 * This script automatically fills the health quiz form when users arrive
 * at the product page via email links (e.g., "Finish Barnaby's Plan").
 * 
 * It reads URL parameters and populates the quiz form accordingly.
 */

(function() {
  'use strict';

  // Wait for DOM to be ready (using shared utility)
  if (typeof window.domReady === 'function') {
    window.domReady(init);
  } else {
    // Fallback if utility not loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }

  function getQuizCompletedWithExpiry() {
    try {
      const raw = localStorage.getItem('junoQuizCompleted') || sessionStorage.getItem('junoQuizCompleted');
      if (!raw) return false;
      if (raw === 'true') return true;
      const data = JSON.parse(raw);
      if (data && data.completed) {
        if (!data.timestamp || (Date.now() - data.timestamp) <= 7 * 24 * 60 * 60 * 1000) return true;
      }
      localStorage.removeItem('junoQuizCompleted');
      sessionStorage.removeItem('junoQuizCompleted');
      return false;
    } catch (e) { return false; }
  }

  function init() {
    // Check if quiz was just completed - if so, don't auto-open (7-day expiry for security)
    if (getQuizCompletedWithExpiry()) {
      // Still allow autofill if parameters exist, but don't auto-open
      if (window.urlParams && window.urlParams.hasQuizParams()) {
        const quizData = window.urlParams.getQuizData();
        window.junoQuizAutofillData = quizData;
        return;
      }
      return;
    }
    
    // Use URL params utility if available, otherwise fallback
    let hasQuizParams = false;
    let quizData = null;
    
    if (window.urlParams) {
      hasQuizParams = window.urlParams.hasQuizParams();
      if (hasQuizParams) {
        quizData = window.urlParams.getQuizData();
      }
    } else {
      // Fallback: direct URLSearchParams usage
      const urlParams = new URLSearchParams(window.location.search);
      hasQuizParams = urlParams.has('dogName') || urlParams.has('name') ||
                     urlParams.has('breed') || urlParams.has('age') ||
                     urlParams.has('weight') || urlParams.has('size') ||
                     urlParams.has('email') || urlParams.has('issues') ||
                     urlParams.has('diet');
      
      if (hasQuizParams) {
        // Helper function to capitalize first letter of name
        function capitalizeName(name) {
          if (!name || typeof name !== 'string') return name;
          var trimmed = name.trim();
          if (trimmed.length === 0) return trimmed;
          // Typed in one case ("bella rose", "BELLA"): capitalise each word, split on spaces and hyphens,
          // plus the letter after a single-letter prefix like O' or D' ("o'malley" -> "O'Malley").
          if (trimmed === trimmed.toLowerCase() || trimmed === trimmed.toUpperCase()) {
            return trimmed.toLowerCase().split(/([\s-]+)/).map(function(part) {
              if (!part || /^[\s-]+$/.test(part)) return part;
              var first = part.charAt(0);
              part = first.toUpperCase() + part.slice(1);
              if (part.length > 2 && (part.charAt(1) === "'" || part.charAt(1) === '\u2019') && first.toLowerCase() !== first.toUpperCase()) {
                part = part.slice(0, 2) + part.charAt(2).toUpperCase() + part.slice(3);
              }
              return part;
            }).join('');
          }
          // Mixed case ("McFly", "Bella Rose"): keep exactly what they typed, just start with a capital.
          return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
        }
        
        const rawName = urlParams.get('dogName') || urlParams.get('name');
        quizData = {
          name: capitalizeName(rawName),
          breed: urlParams.get('breed'),
          age: urlParams.get('age'),
          weight: urlParams.get('weight') || urlParams.get('size'),
          email: urlParams.get('email'),
          issues: urlParams.get('issues') ? urlParams.get('issues').split(',').map(i => i.trim()) : [],
          diet: urlParams.get('diet')
        };
      }
    }

    if (!hasQuizParams || !quizData) {
      return; // No parameters, exit early
    }

    // Check if we should auto-open the quiz
    // Only auto-open if explicitly requested (openQuiz=true) or if coming from email (dogName param)
    // Don't auto-open if just coming from quiz redirect (which uses 'name' param)
    let autoOpen = false;
    if (window.urlParams) {
      autoOpen = window.urlParams.get('openQuiz') === 'true' || window.urlParams.get('dogName') !== null;
    } else {
      // Fallback
      const urlParams = new URLSearchParams(window.location.search);
      autoOpen = urlParams.get('openQuiz') === 'true' || urlParams.has('dogName');
    }

    if (window.JunoQuiz && typeof window.JunoQuiz.open === 'function') {
      setupAutofill(quizData, autoOpen);
      return;
    }
    // Poll for quiz to be ready
    let attempts = 0;
    const maxAttempts = 50;
    const checkInterval = setInterval(function() {
      attempts++;
      if (window.JunoQuiz && typeof window.JunoQuiz.open === 'function') {
        clearInterval(checkInterval);
        setupAutofill(quizData, autoOpen);
        return;
      }
      const quizModal = document.getElementById('JunoQuizModal');
      if (quizModal) {
        clearInterval(checkInterval);
        setupAutofill(quizData, autoOpen);
        return;
      }
      if (attempts >= maxAttempts) {
        clearInterval(checkInterval);
      }
    }, 100);
  }

  function setupAutofill(quizData, autoOpen) {
    // Store the data for when quiz opens (make it globally accessible)
    window.junoQuizAutofillData = quizData;

    // Override or extend the quiz's open function to autofill
    if (window.JunoQuiz && typeof window.JunoQuiz.open === 'function') {
      const originalOpen = window.JunoQuiz.open;
      
      window.JunoQuiz.open = function() {
        originalOpen.call(this);
        
        // Fill form after modal is fully open
        setTimeout(function() {
          window.fillQuizForm(quizData);
        }, 300);
      };

      // Auto-open if requested
      if (autoOpen) {
        setTimeout(function() {
          if (window.JunoQuiz) {
            window.JunoQuiz.open();
          }
        }, 800); // Increased delay to ensure page is ready
      }
    } else {
      // Fallback: Listen for quiz open events
      const quizModal = document.getElementById('JunoQuizModal');
      if (quizModal) {
        const observer = new MutationObserver(function(mutations) {
          mutations.forEach(function(mutation) {
            if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
              const isOpen = quizModal.classList.contains('is-open');
              if (isOpen && !quizModal.dataset.autofilled) {
                quizModal.dataset.autofilled = 'true';
                setTimeout(function() {
                  window.fillQuizForm(quizData);
                }, 200);
              }
            }
          });
        });

        observer.observe(quizModal, {
          attributes: true,
          attributeFilter: ['class']
        });

        // Auto-open if requested
        if (autoOpen) {
          setTimeout(function() {
            // Try to trigger quiz open via click event
            const openButtons = document.querySelectorAll('.js-open-juno-quiz');
            if (openButtons.length > 0) {
              openButtons[0].click();
            } else if (window.JunoQuiz && typeof window.JunoQuiz.open === 'function') {
              window.JunoQuiz.open();
            }
          }, 500);
        }
      }
    }
  };
  
  window.fillQuizForm = function(data) {
    if (!data) data = window.junoQuizAutofillData;
    if (!data) return;
    
    // Fill Step 1: Dog Name
    if (data.name) {
      const nameInput = document.getElementById('q-dog-name');
      if (nameInput) {
        nameInput.value = data.name;
        nameInput.dispatchEvent(new Event('input', { bubbles: true }));
        nameInput.dispatchEvent(new Event('change', { bubbles: true }));
        
        // Update name displays if they exist
        const nameDisplays = document.querySelectorAll('.dog-name-display');
        nameDisplays.forEach(function(el) {
          el.textContent = data.name;
        });
      }
    }

    // Fill Step 2: Dog Breed
    if (data.breed) {
      const breedInput = document.getElementById('q-dog-breed');
      if (breedInput) {
        breedInput.value = data.breed;
        breedInput.dispatchEvent(new Event('input', { bubbles: true }));
        breedInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // Fill Step 3: Dog Age (select dropdown)
    if (data.age) {
      const ageSelect = document.getElementById('q-dog-age');
      if (ageSelect) {
        // Try exact value match first
        const exactOption = Array.from(ageSelect.options).find(function(opt) {
          return opt.value === data.age || opt.value === String(data.age);
        });
        
        if (exactOption) {
          ageSelect.value = exactOption.value;
        } else {
          // Try partial match (e.g., "Puppy" matches "0 to 1 year (Puppy)")
          const partialMatch = Array.from(ageSelect.options).find(function(opt) {
            return opt.text.toLowerCase().includes(data.age.toLowerCase()) ||
                   data.age.toLowerCase().includes(opt.value.toLowerCase());
          });
          
          if (partialMatch) {
            ageSelect.value = partialMatch.value;
          }
        }
        
        // Trigger change event
        ageSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // Fill Step 4: Dog Weight/Size (radio buttons)
    if (data.weight) {
      const weightValue = data.weight.trim();
      
      // Try exact match first (use find to avoid selector injection from URL params)
      const weightRadios = document.querySelectorAll('input[name="dog_weight"]');
      const weightRadio = Array.from(weightRadios).find(function(r) { return r.value === weightValue; });
      
      if (weightRadio) {
        weightRadio.checked = true;
        weightRadio.dispatchEvent(new Event('change', { bubbles: true }));
        weightRadio.dispatchEvent(new Event('click', { bubbles: true }));
      } else {
        // Try to match by label text or card title
        let found = false;
        weightRadios.forEach(function(radio) {
          const label = radio.closest('label');
          if (label) {
            const cardTitle = label.querySelector('.quiz-card-title');
            const cardText = cardTitle ? cardTitle.textContent.toLowerCase() : '';
            const weightLower = weightValue.toLowerCase();
            
            if (cardText.includes(weightLower) || 
                weightLower.includes(cardText.split(' ')[0]) ||
                radio.value.toLowerCase() === weightLower) {
              radio.checked = true;
              radio.dispatchEvent(new Event('change', { bubbles: true }));
              radio.dispatchEvent(new Event('click', { bubbles: true }));
              found = true;
            }
          }
        });
      }
    }

    // Fill Step 5: Issues (checkboxes)
    if (data.issues && data.issues.length > 0) {
      data.issues.forEach(function(issue) {
        const issueCheckboxes = document.querySelectorAll('input[name="dog_issues"]');
        const issueCheckbox = Array.from(issueCheckboxes).find(function(c) { return c.value === issue; });
        if (!issueCheckbox) {
          // Try to match by label text
          issueCheckboxes.forEach(function(checkbox) {
            const label = checkbox.closest('label') || document.querySelector(`label[for="${checkbox.id}"]`);
            if (label && label.textContent.toLowerCase().includes(issue.toLowerCase())) {
              checkbox.checked = true;
            }
          });
        } else {
          issueCheckbox.checked = true;
        }
      });
    }

    // Fill Step 6: Diet (radio buttons)
    if (data.diet) {
      const dietRadios = document.querySelectorAll('input[name="dog_diet"]');
      const dietRadio = Array.from(dietRadios).find(function(r) { return r.value === data.diet; });
      if (!dietRadio) {
        // Try to match by label text
        dietRadios.forEach(function(radio) {
          const label = radio.closest('label') || document.querySelector(`label[for="${radio.id}"]`);
          if (label && label.textContent.toLowerCase().includes(data.diet.toLowerCase())) {
            radio.checked = true;
          }
        });
      } else {
        dietRadio.checked = true;
      }
    }

    // Fill Step 8: Email
    if (data.email) {
      const emailInput = document.getElementById('q-email');
      if (emailInput) {
        emailInput.value = data.email;
      }
    }

    // Trigger any change events to ensure form validation works
    const inputs = document.querySelectorAll('#JunoQuizModal input, #JunoQuizModal select');
    inputs.forEach(function(input) {
      if (input.value || input.checked) {
        input.dispatchEvent(new Event('change', { bubbles: true }));
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    });
    
    // Also update name displays if name was filled
    if (data.name) {
      const nameDisplays = document.querySelectorAll('.dog-name-display');
      nameDisplays.forEach(function(el) {
        el.textContent = data.name;
      });
    }
  }

  // Also handle direct form access (if quiz is already open)
  document.addEventListener('DOMContentLoaded', function() {
    if (window.junoQuizAutofillData) {
      const quizModal = document.getElementById('JunoQuizModal');
      if (quizModal && quizModal.classList.contains('is-open')) {
        setTimeout(function() {
          window.fillQuizForm(window.junoQuizAutofillData);
        }, 200);
      }
    }
  });

  // Listen for quiz opening events (even if opened manually)
  // This ensures autofill works even if quiz is opened after page load.
  function attachObserverToModal(quizModal) {
    if (!quizModal || !window.junoQuizAutofillData || quizModal.dataset.autofillObserverAttached) return;
    quizModal.dataset.autofillObserverAttached = 'true';

    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const isOpen = quizModal.classList.contains('is-open');
          if (isOpen && window.junoQuizAutofillData && !quizModal.dataset.autofilled) {
            quizModal.dataset.autofilled = 'true';
            setTimeout(function() {
              window.fillQuizForm(window.junoQuizAutofillData);
            }, 300);
          }
        }
      });
    });

    observer.observe(quizModal, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  function setupQuizObserver() {
    const quizModal = document.getElementById('JunoQuizModal');
    if (quizModal) {
      attachObserverToModal(quizModal);
      return;
    }
    // Modal not in DOM yet - watch body for when it appears
    const bodyObserver = new MutationObserver(function(mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var nodes = mutations[i].addedNodes;
        for (var j = 0; j < nodes.length; j++) {
          var node = nodes[j];
          if (node.nodeType === 1) {
            var modal = node.id === 'JunoQuizModal' ? node : (node.querySelector && node.querySelector('#JunoQuizModal'));
            if (modal) {
              bodyObserver.disconnect();
              attachObserverToModal(modal);
              return;
            }
          }
        }
      }
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  // Set up observer when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupQuizObserver);
  } else {
    setupQuizObserver();
  }
})();

